import React, { createContext, useContext, useEffect, useState } from 'react';
import { setLocale } from '../utils/format';

export type Lang = 'sr' | 'en';

interface LanguageContextType {
  lang: Lang;
  setLang: (l: Lang) => void;
}

const LanguageContext = createContext<LanguageContextType>({ lang: 'sr', setLang: () => {} });

const LOCALES: Record<Lang, string> = { sr: 'sr-RS', en: 'en-US' };

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(() => (localStorage.getItem('vs-lang') as Lang) || 'sr');

  useEffect(() => {
    localStorage.setItem('vs-lang', lang);
    setLocale(LOCALES[lang]);
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  // Ensure formatter locale is set on first render (before the effect runs).
  setLocale(LOCALES[lang]);

  return <LanguageContext.Provider value={{ lang, setLang: setLangState }}>{children}</LanguageContext.Provider>;
};

export function useLang(): LanguageContextType {
  return useContext(LanguageContext);
}

/**
 * Inline translator. Usage: `const t = useT(); t('Sačuvaj', 'Save')`.
 * Serbian is the source language; English is the second argument.
 */
export function useT(): (sr: string, en: string) => string {
  const { lang } = useLang();
  return (sr: string, en: string) => (lang === 'en' ? en : sr);
}
